import { useAppSelector } from "@app/store";
import CardFrozen from "@assets/icons/card-frozen.svg";
import Coin from "@assets/icons/coin.svg";
import Lock from "@assets/icons/lock.svg";
import WalletMoney from "@assets/icons/wallet-money.svg";
import { Block } from "@components/block";
import { Button } from "@components/button";
import { useBalanceDispatch } from "@features/balance";
import { formatRubles, formatUsdt } from "@helpers/format";
import classNames from "classnames";
import { memo, useEffect } from "react";
import styles from "./balance.module.scss";

type BaseProps = { className?: string; balance: number; withRubles?: boolean };

type MerchantBalanceProps = BaseProps & {
  onClickHistory: () => void;
  onClickWithdraw: () => void;
};

export const MerchantBalance = ({
  onClickWithdraw,
  onClickHistory,
  className,
  balance,
  withRubles,
}: MerchantBalanceProps) => {
  return (
    <Block className={classNames(styles.card, className)}>
      <BalanceContent
        type={"wallet"}
        balance={balance}
        withRubles={withRubles}
      />
      <Button
        onClick={onClickHistory}
        className={styles.buttonHistory}
        type="outline"
      >
        История выводов
      </Button>

      <Button className={styles.buttonWithdraw} onClick={onClickWithdraw}>
        Вывести
      </Button>
    </Block>
  );
};

type TraderBalanceProps = BaseProps & {
  onClick?: () => void;
  type: "trusted" | "frozen" | "profit";
  saveBalance?: number;
};

export const TraderBalance = ({
  type,
  onClick,
  className,
  balance,
  saveBalance,
  withRubles,
}: TraderBalanceProps) => {
  return (
    <Block className={classNames(styles.card, className)}>
      <BalanceContent
        type={type}
        balance={balance}
        withRubles={withRubles}
        saveBalance={saveBalance}
      />
      {!!onClick && (
        <Button
          onClick={onClick}
          className={styles.button}
          type={["trusted", "profit"].includes(type) ? undefined : "outline"}
        >
          {type === "trusted" && "Пополнить"}
          {type === "frozen" && "Подробнее"}
          {type === "profit" && "Вывести"}
        </Button>
      )}
    </Block>
  );
};

type BalanceContentProps = Pick<TraderBalanceProps, "saveBalance"> &
  Pick<BaseProps, "balance" | "withRubles"> & {
    type: TraderBalanceProps["type"] | "wallet";
  };

const BalanceContent = ({
  balance,
  type,
  saveBalance,
  withRubles = true,
}: BalanceContentProps) => {
  const roundedBalance = Math.round(balance * 100) / 100;
  const roundedSaveBalance = saveBalance != null ? Math.round(saveBalance * 100) / 100 : 0;
  return (
    <div className={styles.header}>
      <div className={styles.headerTitle}>
        <h3>
          {type === "frozen" && "Замороженный баланс"}
          {type === "wallet" && "Баланс кошелька"}
          {type === "trusted" && "Трастовый баланс"}
          {type === "profit" && "Прибыль со сделок"}
        </h3>
        <div
          className={classNames(
            styles.icon,
            ["trusted", "wallet"].includes(type) && styles.trustedIcon,
            type === "frozen" && styles.frozenIcon,
            type === "profit" && styles.profitIcon
          )}
        >
          {["trusted", "wallet"].includes(type) && <Coin />}
          {type === "frozen" && <CardFrozen />}
          {type === "profit" && <WalletMoney />}
        </div>
      </div>

      <div className={styles.balanceContainer}>
        <p className={styles.balance}>{formatUsdt(roundedBalance)}</p>
        <div className={styles.buttonBalanceContainer}>
          {withRubles && <RublesBalance balance={roundedBalance} />}
          {!!saveBalance && (
            <div className={styles.saveBalanceContainer}>
              <span className={styles.saveBalance}>
                {formatUsdt(roundedSaveBalance)}
              </span>
              <Lock />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const RublesBalance = memo(({ balance }: Pick<BaseProps, "balance">) => {
  const usdtPrice = useAppSelector((state) => state.balance.usdtPrice);
  const loadingUsdtPrice = useAppSelector(
    (state) => state.balance.loadingUsdtPrice
  );
  const { getUsdtPrice } = useBalanceDispatch();

  const rublesBalance =
    loadingUsdtPrice || !usdtPrice
      ? "..."
      : formatRubles(parseFloat((usdtPrice * balance).toFixed(2)));

  useEffect(() => {
    if (!usdtPrice) {
      getUsdtPrice();
    }
  }, [getUsdtPrice, usdtPrice]);

  return <p className={styles.balanceRub}>{rublesBalance}</p>;
});
